import { Button } from "primereact/button";

import { EmptyStateIllustration } from "./empty-state-illustration";
import styles from "./empty-state.module.scss";

/** Título e mensagem orientativa de uma das situações de listagem vazia. */
export interface EmptyStateContent {
  title: string;
  description: string;
}

/** Base vazia, busca sem resultados ou filtros sem resultados. */
export type EmptyStateKind = "empty" | "search" | "filter";

interface EmptyStateProps {
  /** Situação da consulta: define ilustração, texto e o botão de limpar. */
  kind: EmptyStateKind;
  content: EmptyStateContent;
  /** Esvazia o termo de busca ou os filtros, conforme o `kind`. */
  onClear?: () => void;
  testId: string;
}

const EMPTY_ICON = "pi pi-inbox";

const CLEAR_LABEL: Record<Exclude<EmptyStateKind, "empty">, string> = {
  search: "Limpar Busca",
  filter: "Limpar Filtros",
};

/**
 * Busca no input tem precedência sobre os filtros: os dois critérios ativos
 * ainda são a tela de busca vazia, como no Figma (`ilu-no-search`).
 */
export function resolveEmptyStateKind(hasSearch: boolean, hasFilters: boolean): EmptyStateKind {
  if (hasSearch) return "search";
  if (hasFilters) return "filter";
  return "empty";
}

/**
 * Estado vazio da área de resultados das listagens do Painel de Controle: ocupa
 * o lugar da tabela sem linhas para que a ausência de registros seja lida como
 * resultado da consulta, e não como erro ou falha de carregamento.
 */
export function EmptyState({ kind, content, onClear, testId }: EmptyStateProps) {
  const showsClear = kind !== "empty" && Boolean(onClear);

  return (
    <div className={styles.emptyState} role="status" data-testid={testId} data-variant={kind}>
      {kind === "empty" ? (
        <span className={styles.iconCircle} aria-hidden data-illustration="empty">
          <i className={`${EMPTY_ICON} ${styles.icon}`} />
        </span>
      ) : (
        <EmptyStateIllustration kind={kind} />
      )}

      <div className={styles.copy}>
        <p className={styles.title}>{content.title}</p>
        <p className={styles.description}>{content.description}</p>
      </div>

      {showsClear ? (
        <Button
          type="button"
          className={styles.clearButton}
          label={CLEAR_LABEL[kind]}
          onClick={onClear}
          data-testid={`${testId}-clear`}
        />
      ) : null}
    </div>
  );
}
